import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import { isSubtaskElement } from '../../../shared/domain/flows/typeGuards.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { filter } from '../../../shared/lib/functional/array.no-deps'
import { getAllElements } from '../../../shared/domain/flows/element.no-deps'
import type { LoopPath } from '../../../shared/domain/flows/LoopPath.no-deps'
import { isNil, isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import { retrySafe } from '../../../shared/lib/retry.no-deps'

export const elementDomId = (id: Flows.ElementId, loopPath: LoopPath) => `element-dom-id_${id}_${loopPath}`
export const elementWithAnyLoopPathSelector = (id: Flows.ElementId) => `[id^=element-dom-id_${id}]`

export function tryScrollToElement(
    id: Flows.ElementId,
    loopPath: LoopPath,
    behavior: ScrollBehavior,
    retries: number,
    requireLoopPath: boolean = true
) {
    retrySafe(retries, async () => {
        const target = findElementInDocument(id, loopPath, requireLoopPath)
        if (isNil(target)) {
            return false
        }
        target.scrollIntoView({ behavior })
        return true
    })
}

export function findElementInDocument(id: Flows.ElementId, loopPath: LoopPath, requireLoopPath: boolean = true) {
    const matchWithLoopPath = document.getElementById(elementDomId(id, loopPath))
    if (isNotNil(matchWithLoopPath)) {
        return matchWithLoopPath
    }
    if (requireLoopPath) {
        return null
    }

    const matchWithoutLoopPath = document.querySelectorAll(elementWithAnyLoopPathSelector(id))[0]
    if (isNotNil(matchWithoutLoopPath)) {
        return matchWithoutLoopPath
    }
    return null
}

export function parseElementDomId(domId: string) {
    const [, elementId, loopPath] = domId.split('_')
    return {
        elementId: elementId as Flows.ElementId,
        loopPath: loopPath as LoopPath,
    }
}

export function shouldScroll(element: Flows.Element | Flows.NestedElement): boolean {
    switch (element.type) {
        case 'radio':
        case 'checkbox':
        case 'image-select':
        case 'dropdown':
            return element.maxChoices && element.maxChoices === 1
        case 'yes-no':
        case 'confirmation':
            return true
        default:
            return false
    }
}

export function getSubtaskElements(flow: Flows.Flow): Flows.SubtaskElement[] {
    return pipe(getAllElements(flow), filter(isSubtaskElement))
}
